import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import api from '../services/api';

const initialForm = {
  name: '',
  email: '',
  whatsapp_number: '',
  instance_name: '',
};

const s = {
  title: { fontSize: 22, fontWeight: 600, color: '#f1f5f9', marginBottom: 6 },
  sub: { fontSize: 13, color: '#94a3b8', marginBottom: 24 },
  panel: { marginBottom: 24, padding: 18, border: '1px solid #334155', borderRadius: 8, background: '#162234' },
  form: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))', gap: 12, alignItems: 'end' },
  label: { display: 'block', fontSize: 12, color: '#94a3b8', marginBottom: 6 },
  input: { width: '100%', minHeight: 38, boxSizing: 'border-box', borderRadius: 6, border: '1px solid #334155', background: '#0f172a', color: '#f8fafc', padding: '8px 10px', outline: 'none' },
  actions: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  btn: { minHeight: 38, border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  primary: { background: '#0ea5e9', color: '#fff' },
  secondary: { background: '#334155', color: '#e2e8f0' },
  danger: { background: '#991b1b', color: '#fff' },
  disabled: { opacity: 0.55, cursor: 'not-allowed' },
  online: { background: 'rgba(34,197,94,.16)', color: '#4ade80', border: '1px solid rgba(34,197,94,.35)' },
  offline: { background: 'rgba(239,68,68,.14)', color: '#f87171', border: '1px solid rgba(239,68,68,.35)' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 6, borderRadius: 999, padding: '4px 9px', fontSize: 11, fontWeight: 700, marginTop: 8 },
  qrBox: { marginTop: 14, padding: 12, border: '1px solid #334155', borderRadius: 8, background: '#0f172a' },
  qrImg: { width: 180, height: 180, objectFit: 'contain', background: '#fff', borderRadius: 8, padding: 8, display: 'block', marginTop: 8 },
  msg: { marginTop: 12, fontSize: 13 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 },
  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 20 },
  cardTop: { display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 4 },
  name: { fontSize: 16, fontWeight: 600, color: '#f1f5f9', overflowWrap: 'anywhere' },
  inst: { fontSize: 12, color: '#94a3b8', marginBottom: 8, overflowWrap: 'anywhere' },
  meta: { fontSize: 12, color: '#64748b', marginBottom: 16, overflowWrap: 'anywhere' },
  stat: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  kpi: { fontSize: 11, color: '#94a3b8' },
  kpiV: { fontSize: 20, fontWeight: 700, color: '#38bdf8', display: 'block' },
  empty: { border: '1px dashed #334155', borderRadius: 8, padding: 20, color: '#94a3b8' },
};

export default function Atendentes() {
  const [ats, setAts] = useState([]);
  const [instancias, setInstancias] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [qrMap, setQrMap] = useState({});
  const [connectingId, setConnectingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const carregar = useCallback(async () => {
    const token = localStorage.getItem('waflow_token');
    const directApi = axios.create({
      baseURL: `${window.location.origin}/api`,
      timeout: 15000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    const [atendentesResp, statusResp] = await Promise.all([
      directApi.get('/atendentes'),
      directApi.get('/gestor/status').catch(() => ({ data: [] })),
    ]);
    setAts(atendentesResp.data || []);
    setInstancias(statusResp.data || []);
    setStatusMap(Object.fromEntries((statusResp.data || [])
      .filter((inst) => inst.instance_name)
      .map((inst) => [inst.instance_name, inst.status || inst.state || inst.connectionStatus || inst.connection_state || 'unknown'])));
  }, []);

  useEffect(() => {
    carregar().catch(() => setMessage({ type: 'error', text: 'Erro ao carregar atendentes.' }));
  }, [carregar]);

  const instanciaOptions = useMemo(() => (
    instancias.map((inst) => inst.instance_name).filter(Boolean)
  ), [instancias]);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const isOnline = (status) => ['open', 'connected', 'online'].includes(String(status || '').toLowerCase());
  const statusAtendente = (atendente) => {
    if (!atendente.instance_name) return { label: 'Sem instância', online: false, raw: 'sem_instancia' };
    const raw = statusMap[atendente.instance_name] || 'unknown';
    return { label: isOnline(raw) ? 'Online' : 'Offline', online: isOnline(raw), raw };
  };

  const qrAsImage = (qr) => {
    if (!qr || typeof qr !== 'string') return null;
    if (qr.startsWith('data:image')) return qr;
    if (/^[A-Za-z0-9+/=]+$/.test(qr) && qr.length > 100) return `data:image/png;base64,${qr}`;
    return null;
  };

  const salvar = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setMessage({ type: 'error', text: 'Informe o nome do atendente.' });
      return;
    }

    setLoading(true);
    setMessage(null);
    try {
      await api.post('/atendentes', {
        name: form.name.trim(),
        email: form.email.trim() || null,
        whatsapp_number: form.whatsapp_number.replace(/\D/g, '') || null,
        instance_name: form.instance_name.trim() || null,
      });
      setForm(initialForm);
      setMessage({ type: 'success', text: 'Atendente cadastrado.' });
      await carregar();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao cadastrar atendente.' });
    } finally {
      setLoading(false);
    }
  };

  const excluir = async (atendente) => {
    const ok = window.confirm(`Excluir o atendente "${atendente.name}"?`);
    if (!ok) return;

    setLoading(true);
    setMessage(null);
    try {
      await api.delete(`/atendentes/${atendente.id}`);
      setMessage({ type: 'success', text: 'Atendente excluido.' });
      await carregar();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao excluir atendente.' });
    } finally {
      setLoading(false);
    }
  };

  const conectar = async (atendente) => {
    if (!atendente.instance_name) {
      setMessage({ type: 'error', text: 'Este atendente não tem instância Evolution vinculada.' });
      return;
    }
    setConnectingId(atendente.id);
    setMessage(null);
    try {
      const { data } = await api.post(`/atendentes/${atendente.id}/conectar`);
      setStatusMap((current) => ({ ...current, [atendente.instance_name]: data.status || current[atendente.instance_name] || 'unknown' }));
      setQrMap((current) => ({ ...current, [atendente.id]: data.qrcode || '' }));
      setMessage({ type: 'success', text: data.online ? 'Instância conectada.' : 'QR Code gerado. Leia com o WhatsApp para conectar.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao gerar QR Code.' });
    } finally {
      setConnectingId(null);
    }
  };

  const sincronizarEvolution = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const token = localStorage.getItem('waflow_token');
      const { data } = await axios.post(
        `${window.location.origin}/api/gestor/instancias/sync`,
        {},
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, timeout: 15000 }
      );
      const novos = data.atendentes_criados?.length || 0;
      setMessage({ type: 'success', text: `Evolution sincronizada. ${novos} atendente(s) novo(s).` });
      await carregar();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Erro ao sincronizar Evolution.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div style={s.title}>Atendentes</div>
      <div style={s.sub}>Cadastre operadores e vincule cada um a uma instancia da Evolution API. Versao 2026-06-17 19:31.</div>

      <form style={s.panel} onSubmit={salvar}>
        <div style={s.form}>
          <div>
            <label style={s.label} htmlFor="atendente-name">Nome</label>
            <input id="atendente-name" style={s.input} value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Comercial 02" />
          </div>
          <div>
            <label style={s.label} htmlFor="atendente-email">Email</label>
            <input id="atendente-email" style={s.input} value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="comercial02@grupod1.com.br" />
          </div>
          <div>
            <label style={s.label} htmlFor="atendente-phone">Telefone WhatsApp</label>
            <input id="atendente-phone" style={s.input} value={form.whatsapp_number} onChange={(e) => setField('whatsapp_number', e.target.value)} placeholder="5514999999999" />
          </div>
          <div>
            <label style={s.label} htmlFor="atendente-instance">Instancia Evolution</label>
            <input id="atendente-instance" list="instancias-evolution" style={s.input} value={form.instance_name} onChange={(e) => setField('instance_name', e.target.value)} placeholder="Selecione ou digite" />
            <datalist id="instancias-evolution">
              {instanciaOptions.map((name) => <option key={name} value={name} />)}
            </datalist>
          </div>
          <div style={s.actions}>
            <button type="button" style={{ ...s.btn, ...s.secondary, ...(loading ? s.disabled : {}) }} onClick={sincronizarEvolution} disabled={loading}>
              Sincronizar Evolution
            </button>
            <button type="submit" style={{ ...s.btn, ...s.primary, ...(loading ? s.disabled : {}) }} disabled={loading}>
              Cadastrar
            </button>
            <button type="button" style={{ ...s.btn, ...s.secondary }} onClick={() => setForm(initialForm)} disabled={loading}>
              Limpar
            </button>
          </div>
        </div>
        {message && (
          <div style={{ ...s.msg, color: message.type === 'error' ? '#f87171' : '#34d399' }}>
            {message.text}
          </div>
        )}
      </form>

      {ats.length === 0 ? (
        <div style={s.empty}>Nenhum atendente cadastrado.</div>
      ) : (
        <div style={s.grid}>
          {ats.map((a) => {
            const st = statusAtendente(a);
            const qr = qrMap[a.id];
            const qrImg = qrAsImage(qr);
            return (
            <div key={a.id} style={s.card}>
              <div style={s.cardTop}>
                <div>
                  <div style={s.name}>{a.name}</div>
                  <div style={s.inst}>{a.instance_name || 'Sem instancia'} · {a.active ? 'Ativo' : 'Inativo'}</div>
                  <div style={s.meta}>{a.email || 'Sem email'} · {a.whatsapp_number || 'Sem telefone'}</div>
                  <span style={{ ...s.badge, ...(st.online ? s.online : s.offline) }}>
                    {st.online ? 'Online' : 'Offline'} {st.raw && st.raw !== 'unknown' ? `(${st.raw})` : ''}
                  </span>
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
                  {!st.online && a.instance_name && (
                    <button type="button" style={{ ...s.btn, ...s.primary, ...(connectingId === a.id ? s.disabled : {}) }} onClick={() => conectar(a)} disabled={connectingId === a.id}>
                      {connectingId === a.id ? 'Gerando...' : 'Conectar'}
                    </button>
                  )}
                  <button type="button" style={{ ...s.btn, ...s.danger, ...(loading ? s.disabled : {}) }} onClick={() => excluir(a)} disabled={loading}>
                    Excluir
                  </button>
                </div>
              </div>
              <div style={s.stat}>
                <div style={s.kpi}><span style={s.kpiV}>{a.conversas_hoje || 0}</span>Conv. hoje</div>
                <div style={s.kpi}><span style={s.kpiV}>{a.enviadas_hoje || 0}</span>Enviadas hoje</div>
                <div style={s.kpi}><span style={{ ...s.kpiV, color: '#ef4444' }}>{a.sem_retorno || 0}</span>Sem retorno</div>
              </div>
              {qr && (
                <div style={s.qrBox}>
                  <div style={{ fontSize:12, color:'#94a3b8', fontWeight:700 }}>QR Code para conectar</div>
                  {qrImg ? (
                    <img style={s.qrImg} src={qrImg} alt={`QR Code ${a.name}`} />
                  ) : (
                    <pre style={{ whiteSpace:'pre-wrap', color:'#cbd5e1', fontSize:12, margin:0, marginTop:8 }}>{String(qr)}</pre>
                  )}
                </div>
              )}
            </div>
          )})}
        </div>
      )}
    </div>
  );
}
